import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star'; // For beautiful results/benefits
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal'; // For quick fixes/cosmetic
import SavingsIcon from '@mui/icons-material/Savings'; // For cost-effectiveness
import PaletteIcon from '@mui/icons-material/Palette'; // For shade matching/aesthetics
import BrushIcon from '@mui/icons-material/Brush'; // For application/sculpting
import SpeedIcon from '@mui/icons-material/Speed'; // For single visit
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For "Is it right for you?"
import HandymanIcon from '@mui/icons-material/Handyman'; // For repairs/chips
import ArtTrackIcon from '@mui/icons-material/ArtTrack'; // For artistry in "Why Choose Us"
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const DentalBondingPacificaDental = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'A minimally invasive procedure, often requiring no enamel removal.',
    'Cost-effective solution for various minor cosmetic dental flaws.',
    'Typically completed in a single, convenient visit to Pacifica Dental.',
    'Versatile in correcting chips, gaps, stains, and minor reshaping.',
    'Provides a natural-looking aesthetic improvement to your smile.',
    'Often requires little to no anesthesia, ensuring a comfortable experience.'
  ];

  const bondingProcess = [
    {
      title: '1. Consultation & Precise Shade Selection',
      description: 'Your visit to Pacifica Dental begins with a consultation to discuss your smile goals. We will carefully select a composite resin shade that perfectly matches your natural teeth for seamless integration.',
      icon: <PaletteIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '2. Minimal Tooth Preparation',
      description: 'The surface of the tooth (or teeth) to be bonded is gently etched or roughened to help the resin adhere. A conditioning liquid is then applied. Anesthesia is usually not necessary for this step.',
      icon: <AutoFixNormalIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} /> // Represents surface prep
    },
    {
      title: '3. Artistic Resin Application & Sculpting',
      description: 'Our skilled dentist will meticulously apply the tooth-colored resin material, skillfully sculpting and molding it to the desired shape to correct the imperfection and achieve an aesthetic look.',
      icon: <BrushIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '4. Hardening with Curing Light',
      description: 'A special high-intensity curing light is used to harden the composite resin, bonding it securely to your tooth structure in a matter of seconds.',
      icon: <SpeedIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} /> // Represents quick hardening
    },
    {
      title: '5. Final Shaping & Polishing',
      description: 'Once hardened, the bonded resin is further trimmed, shaped, and polished to a high luster, ensuring it blends flawlessly with your surrounding natural teeth and provides a comfortable bite.',
      icon: <StarIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    }
  ];

  const concernsImproved = [
    'Minor chips or cracks on teeth.',
    'Small gaps or spaces between teeth.',
    'Discoloration or stains on a tooth surface.',
    'Slightly misshapen or unevenly sized teeth.',
    'Making teeth appear longer.',
    'Protecting exposed tooth roots due to gum recession.'
  ];

  const isRightForYou = [
    'You have minor cosmetic dental imperfections you wish to correct.',
    'You are looking for a conservative and affordable cosmetic treatment.',
    'You prefer a procedure that can often be completed in a single visit.',
    'You have good overall oral health (healthy gums and no major untreated issues).',
    'You understand that bonding may not be as durable or stain-resistant as porcelain veneers.',
    'You are seeking a less invasive alternative to veneers for specific situations.'
  ];

  const caringForBonding = [
    'Maintain excellent oral hygiene: Brush at least twice a day and floss daily.',
    'Be mindful of biting into very hard foods, ice, or using bonded teeth as tools.',
    'Avoid or limit consumption of staining agents like coffee, tea, red wine, and tobacco, especially in the first 48 hours after the procedure.',
    'Attend regular dental check-ups and cleanings at Pacifica Dental for polishing and to monitor the bonding.',
    'If you notice any sharp edges or if the bonding chips, contact us promptly.'
  ];
  
  const faqs = [
    {
      question: 'How long does dental bonding last?',
      answer: 'With proper care and good oral hygiene, dental bonding can last from 5 to 10 years. The longevity also depends on the location of the bonding and your personal habits.'
    },
    {
      question: 'Is the dental bonding procedure painful?',
      answer: 'Dental bonding is typically a painless procedure and often requires no anesthesia, especially if decay is not being removed. You might feel the instruments, but there should be no discomfort.'
    },
    {
      question: 'Does dental bonding stain easily?',
      answer: 'The composite resin used in bonding is more susceptible to staining over time compared to natural enamel or porcelain veneers. Limiting staining foods/drinks and good hygiene can help minimize this.'
    },
    {
        question: 'Can dental bonding chip or break?',
        answer: 'Yes, like natural teeth, bonded restorations can chip or break if subjected to excessive force or trauma. However, a benefit of bonding is that it can often be easily repaired by your dentist at Pacifica Dental.'
    },
    {
        question: 'How does dental bonding compare to porcelain veneers?',
        answer: 'Bonding is generally more conservative (less tooth alteration) and less expensive than veneers. Veneers are more durable, stain-resistant, and typically offer a more comprehensive aesthetic transformation for more significant issues. We can discuss which is best for you at Pacifica Dental.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Bonding | Pacifica Dental | Quick Smile Enhancements</title>
        <meta name="description" content="Enhance your smile with conservative dental bonding at Pacifica Dental. Perfect for chips, gaps, and minor flaws. Quick, affordable results. Call (408) 869-0998." />
        <meta name="keywords" content="dental bonding Pacifica Dental, composite bonding, cosmetic bonding Pacifica Dental, tooth bonding, chipped tooth repair, (408) 869-0998, San Jose CA dental bonding, Bay Area cosmetic dentist" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Enhance Your Smile Instantly with Dental Bonding at Pacifica Dental
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              A Conservative and Artistic Solution for Minor Smile Imperfections
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              Dental bonding at Pacifica Dental offers a simple yet effective way to correct minor cosmetic flaws and achieve a more beautiful smile, often in just one visit. This versatile procedure involves applying a tooth-colored composite resin to your teeth, which is then skillfully sculpted and polished by our dentists to repair chips, close gaps, cover stains, or alter the shape of your teeth. It's a minimally invasive technique that can make a significant difference.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Key Advantages of Dental Bonding
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                           {index === 0 ? <AutoFixNormalIcon sx={{ color: 'white', fontSize: '1.4rem' }} /> : 
                            index === 1 ? <SavingsIcon sx={{ color: 'white', fontSize: '1.4rem' }} /> :
                            <CheckCircleOutlineIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                           }
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Bonding Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    The Dental Bonding Procedure at Pacifica Dental
                  </Typography>
                  {bondingProcess.map((step, index) => (
                    <Box key={index} sx={{ mb: index === bondingProcess.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                         {step.icon}
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* What Can Bonding Improve? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                What Smile Concerns Can Dental Bonding Address?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#2c3e50' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  Dental bonding is an excellent choice for addressing a variety of minor cosmetic dental issues, such as:
                </Typography>
                <Grid container spacing={1}>
                    {concernsImproved.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <HandymanIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />
            
            {/* Is It Right For You? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Is Dental Bonding Your Best Cosmetic Option?
              </Typography>
              <Alert severity="info" icon={<HelpOutlineIcon />} sx={{borderRadius: 1.5, p:2, mb:3 }} elevation={2}>
                <AlertTitle sx={{fontWeight: 'bold'}}>Consult with Our Experts</AlertTitle>
                During a consultation at Pacifica Dental, we will evaluate your smile, discuss your goals, and help you determine if dental bonding is the most suitable treatment to achieve the results you desire.
              </Alert>
              <Grid container spacing={2}>
                {isRightForYou.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper elevation={1} sx={{p:1.5, display: 'flex', alignItems: 'center', height: '100%', borderRadius: 1.5}}>
                      <CheckCircleOutlineIcon color="success" sx={{mr: 1.5, fontSize: '1.8rem'}} />
                      <Typography variant="body1" sx={{fontSize: '1rem'}}>{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Caring For Your Bonded Teeth Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Maintaining Your Beautifully Bonded Teeth
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <List>
                        {caringForBonding.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <CheckCircleOutlineIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Dental Bonding FAQs at Pacifica Dental
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Pacifica Dental Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Pacifica Dental for Your Dental Bonding?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Artistic Skill & Precision", text: "Our dentists at Pacifica Dental combine clinical expertise with an artistic touch to sculpt and shade bonding material for exceptionally natural results.", icon: <ArtTrackIcon color="primary" /> },
                      { title: "Conservative Treatment Philosophy", text: "We prioritize preserving your natural tooth structure, making bonding an ideal minimally invasive option for many cosmetic concerns.", icon: <AutoFixNormalIcon color="primary" /> },
                      { title: "Personalized Attention to Detail", text: "We take the time to understand your goals and meticulously match the bonding material to your existing teeth for a seamless blend.", icon: <PaletteIcon color="primary" /> },
                      { title: "Affordable Smile Enhancements", text: "Dental bonding offers a cost-effective way to achieve significant cosmetic improvements, and we're committed to providing value.", icon: <SavingsIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Ready for a Quick and Beautiful Smile Upgrade?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Discover the transformative power of dental bonding. Contact Pacifica Dental today to schedule your consultation!
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Bonding Consultation
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call Pacifica Dental at <Link to="tel:4088690998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> to discuss how bonding can enhance your smile.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default DentalBondingPacificaDental;